import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle,
} from '../../../../components/ui/Dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../../components/ui/Tabs';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import { Switch } from '../../../../components/ui/Switch';
import { Alert } from '../../../../components/ui/Alert';
import AppIcon, { type IconName } from '../../../../components/AppIcon';
import { DialogErrorAlert, type useDialogError } from '../../../../components/ui/DialogError';
import type { ReflectionPrompt, ReflectionPromptInput } from '../../../../models/preformanceManagement';

type Lang = 'en' | 'ar' | 'fr';
type TextField = 'title' | 'category' | 'question' | 'subtext' | 'placeholder';
const LANGS: Lang[] = ['en', 'ar', 'fr'];
const TEXT_FIELDS: TextField[] = ['title', 'category', 'question', 'subtext', 'placeholder'];

/** Column name for a field in a language: title/en → title, title/ar → titleAr. */
const col = (f: TextField | 'tips', lang: Lang) =>
  (lang === 'en' ? f : `${f}${lang === 'ar' ? 'Ar' : 'Fr'}`) as keyof ReflectionPromptInput;

/** Same class list as Input.jsx so a textarea sits in the form as one control among equals. */
const TEXTAREA_CLASS =
  'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50';

// Column widths on the server (BluHRMSContext: Title 200, Category 100). Capped here so the
// browser stops the keystroke rather than the server refusing the save.
const MAX_LEN: Partial<Record<TextField, number>> = { title: 200, category: 100 };

function emptyInput(displayOrder: number): ReflectionPromptInput {
  return {
    title: '', titleAr: '', titleFr: '',
    category: '', categoryAr: '', categoryFr: '',
    question: '', questionAr: '', questionFr: '',
    subtext: '', subtextAr: '', subtextFr: '',
    placeholder: '', placeholderAr: '', placeholderFr: '',
    tips: [], tipsAr: [], tipsFr: [],
    iconName: 'Award', displayOrder, isActive: true,
  };
}

function fromPrompt(p: ReflectionPrompt, customize: boolean): ReflectionPromptInput {
  return {
    // Customizing a platform default for a company carries ITS key so the new company row
    // shadows it. Editing a row never sends a key; the server ignores it on update anyway.
    promptId: customize ? p.promptId : null,
    title: p.title ?? '', titleAr: p.titleAr ?? '', titleFr: p.titleFr ?? '',
    category: p.category ?? '', categoryAr: p.categoryAr ?? '', categoryFr: p.categoryFr ?? '',
    question: p.question ?? '', questionAr: p.questionAr ?? '', questionFr: p.questionFr ?? '',
    subtext: p.subtext ?? '', subtextAr: p.subtextAr ?? '', subtextFr: p.subtextFr ?? '',
    placeholder: p.placeholder ?? '', placeholderAr: p.placeholderAr ?? '', placeholderFr: p.placeholderFr ?? '',
    tips: p.tips ?? [], tipsAr: p.tipsAr ?? [], tipsFr: p.tipsFr ?? [],
    iconName: p.iconName || 'Award', displayOrder: p.displayOrder, isActive: p.isActive,
  };
}

const norm = (s: string | null | undefined) => (s ?? '').trim();
const normTips = (t: string[] | undefined) => (t ?? []).map((x) => x.trim()).filter(Boolean).join('\n');

/** Same rule as the server's AskedTextDiffers: only the 18 text values, after trimming. */
function askedTextDiffers(a: ReflectionPromptInput, b: ReflectionPromptInput): boolean {
  for (const f of TEXT_FIELDS) {
    for (const lang of LANGS) {
      const k = col(f, lang);
      if (norm(a[k] as string) !== norm(b[k] as string)) return true;
    }
  }
  return normTips(a.tips) !== normTips(b.tips)
    || normTips(a.tipsAr) !== normTips(b.tipsAr)
    || normTips(a.tipsFr) !== normTips(b.tipsFr);
}

interface Props {
  open: boolean;
  /** null = add; a prompt = edit, or customize (a company copy of a platform default) when `customize`. */
  prompt: ReflectionPrompt | null;
  customize: boolean;
  /** The number a NEW prompt starts with: after everything the list already holds. */
  defaultOrder: number;
  /** Numbers other prompts hold, each mapped to that prompt's title. */
  takenOrders: Record<number, string>;
  icons: string[];
  saving: boolean;
  dialogError: ReturnType<typeof useDialogError>;
  onSave: (input: ReflectionPromptInput) => Promise<void>;
  onClose: () => void;
}

const ReflectionPromptEditorDialog: React.FC<Props> = ({ open, prompt, customize, defaultOrder, takenOrders, icons, saving, dialogError, onSave, onClose }) => {
  const { t } = useTranslation();
  const [input, setInput] = useState<ReflectionPromptInput>(emptyInput(defaultOrder));
  const [original, setOriginal] = useState<ReflectionPromptInput>(emptyInput(defaultOrder));
  const [lang, setLang] = useState<Lang>('en');

  useEffect(() => {
    if (!open) return;
    const seed = prompt ? fromPrompt(prompt, customize) : emptyInput(defaultOrder);
    setInput(seed);
    setOriginal(seed);
    setLang('en');
  }, [open, prompt, customize, defaultOrder]);

  // A Customize creates a fresh row, so it never forks; an edit forks once the text differs and
  // answers exist (this company's for a company row, any company's for a platform default).
  const willReplace = !!prompt && !customize && prompt.answerCount > 0 && askedTextDiffers(input, original);

  const set = (k: keyof ReflectionPromptInput, v: string | number | boolean | string[]) =>
    setInput((prev) => ({ ...prev, [k]: v }));

  /** A language tab counts as translated when every REQUIRED field has text in that language. */
  const translated = useMemo(() => {
    const has = (l: Lang) => ['title', 'category', 'question'].every((f) => norm(input[col(f as TextField, l)] as string));
    return { en: has('en'), ar: has('ar'), fr: has('fr') };
  }, [input]);

  const missingEn = TEXT_FIELDS.slice(0, 3).filter((f) => !norm(input[f]));
  // Same rule as the server's DisplayOrderTakenAsync, answered before any request is sent.
  const orderHolder = takenOrders[input.displayOrder];
  const orderError = orderHolder ? t('performance.reflectionPrompts.orderTaken', { title: orderHolder }) : undefined;
  const canSave = missingEn.length === 0 && !orderHolder && !saving;

  const fieldLabel = (f: TextField) => t(`performance.reflectionPrompts.field.${f}`);
  const langLabel = (l: Lang) => t(`performance.reflectionPrompts.lang${l[0].toUpperCase()}${l[1]}`);

  const iconOptions = icons.map((name) => ({ value: name, label: name }));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            {prompt == null
              ? t('performance.reflectionPrompts.add')
              : customize ? t('performance.reflectionPrompts.customize') : t('performance.reflectionPrompts.edit')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4">
          <DialogErrorAlert error={dialogError.error} onDismiss={dialogError.clear} className="mb-0" />

          {customize && (
            <Alert variant="info">{t('performance.reflectionPrompts.platformDefaultHint')}</Alert>
          )}

          {willReplace && (
            <Alert variant="warning">
              {t('performance.reflectionPrompts.replaceWarning', { count: prompt!.answerCount })}
            </Alert>
          )}

          <Tabs value={lang} onValueChange={(v) => setLang(v as Lang)} className="w-full">
            <TabsList className="w-full">
              {LANGS.map((l) => (
                <TabsTrigger
                  key={l}
                  value={l}
                  className="flex-1 gap-2"
                  data-testid={`rp-lang-${l}`}
                  data-translated={translated[l] ? 'true' : 'false'}
                >
                  {langLabel(l)}
                  <AppIcon
                    name={translated[l] ? 'CheckCircle' : 'Circle'}
                    size={14}
                    className={translated[l] ? 'text-emerald-600' : 'text-muted-foreground'}
                    role="img"
                    aria-label={translated[l] ? t('performance.reflectionPrompts.translated') : t('performance.reflectionPrompts.untranslated')}
                  />
                </TabsTrigger>
              ))}
            </TabsList>

            {LANGS.map((l) => {
              // Arabic inputs are RTL regardless of the app direction; the other two are LTR.
              const dir = l === 'ar' ? 'rtl' : 'ltr';
              return (
                <TabsContent key={l} value={l} className="flex flex-col gap-3 pt-3" dir={dir}>
                  {TEXT_FIELDS.map((f) => {
                    const k = col(f, l);
                    const required = l === 'en' && f !== 'subtext' && f !== 'placeholder';
                    const value = (input[k] as string) ?? '';
                    const err = required && !norm(value) && value !== ''
                      ? t('performance.reflectionPrompts.required', { field: fieldLabel(f) })
                      : undefined;
                    return f === 'question' || f === 'subtext' ? (
                      <div key={k} className="space-y-2">
                        <label className="text-sm font-medium text-foreground" htmlFor={`rp-${k}`}>
                          {fieldLabel(f)}
                          {required && <span className="text-destructive ms-1">*</span>}
                        </label>
                        <textarea
                          id={`rp-${k}`}
                          dir={dir}
                          rows={2}
                          className={TEXTAREA_CLASS}
                          value={value}
                          aria-required={required || undefined}
                          aria-invalid={err ? true : undefined}
                          onChange={(e) => set(k, e.target.value)}
                        />
                        {err && <p role="alert" className="text-sm text-destructive">{err}</p>}
                      </div>
                    ) : (
                      <Input
                        key={k}
                        id={`rp-${k}`}
                        dir={dir}
                        label={fieldLabel(f)}
                        required={required}
                        maxLength={MAX_LEN[f]}
                        value={value}
                        error={err}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => set(k, e.target.value)}
                      />
                    );
                  })}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground" htmlFor={`rp-tips-${l}`}>
                      {t('performance.reflectionPrompts.field.tips')}
                    </label>
                    <textarea
                      id={`rp-tips-${l}`}
                      dir={dir}
                      rows={3}
                      className={TEXTAREA_CLASS}
                      value={((input[col('tips', l)] as string[]) ?? []).join('\n')}
                      onChange={(e) => set(col('tips', l), e.target.value.split('\n'))}
                    />
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-border pt-4">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Select
                  label={t('performance.reflectionPrompts.field.icon')}
                  options={iconOptions}
                  value={input.iconName}
                  searchable
                  onChange={(v: string) => set('iconName', v)}
                  data-testid="rp-icon"
                />
              </div>
              <div
                className="w-10 h-10 rounded-lg bg-muted border border-border flex items-center justify-center shrink-0 mb-0.5"
                aria-hidden="true"
              >
                <AppIcon name={input.iconName as IconName} size={20} />
              </div>
            </div>
            <Input
              type="number"
              min={0}
              label={t('performance.reflectionPrompts.field.order')}
              error={orderError}
              value={String(input.displayOrder)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('displayOrder', Math.max(0, Number(e.target.value) || 0))}
              data-testid="rp-order"
            />
            <div className="flex items-end pb-2">
              <Switch
                checked={input.isActive}
                onCheckedChange={(c) => set('isActive', c)}
                label={t('performance.reflectionPrompts.field.active')}
                data-testid="rp-active"
              />
            </div>
          </div>
        </DialogBody>
        <DialogFooter className="justify-center">
          <Button onClick={() => onSave(input)} disabled={!canSave} loading={saving} data-testid="rp-save">
            {t('common:Save')}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common:Cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReflectionPromptEditorDialog;
